import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useDevelopmentalRubric from '../../../../../../../hooks/developmental-rubrics/useDevelopmentalRubrics'
import DataGrid from '../../../../../../table/DataGrid'
import IconTooltipButton from '../../../../../../button/IconTooltipButton'
import TextLog from '../../../../../../text/TextLog'
import TextNote from '../../../../../../text/TextNote'
import { Checkbox, Chip, Tooltip, Typography } from '@mui/material'
import { updateColumnTableCurriculum } from '../../../../../../../store/actions/datatable.actions'
import TextFormatDateRange from '../../../../../../text/TextFormatDateRange'
import { getObjectCurriculumElementsDR } from '../../../../../Assessment/utils'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import ChipStatus from '../../Current/Elements/ChipStatus'

const List = ({ curriculumId, currentCurriculum, onSelected, onSelectedAll, checkIds = [], onEdit }) => {
  const dispatch = useDispatch()
  const roles = useSelector((state) => state?.session.user.roles?.map((r) => r.id))

  const permissions = {
    preview: !roles.some((r) => ['Student', 'Parent'].includes(r)),
    edit: !roles.some((r) => ['Student', 'Parent'].includes(r))
  }
  const years = useSelector((state) => state?.tenant?.years || [])
  const subjects = useSelector((state) => state?.subject?.list || [])
  const status = useSelector(
    (state) => state?.analytics?.analyticsCurriculum?.datasets?.data?.data?.status || []
  )
  const { isLoadingList, getDevelopmentalRubrics } = useDevelopmentalRubric()
  const state = useSelector((state) => state?.filter?.ul)

  const list = useSelector((state) => (state?.dr ? state?.dr[curriculumId]?.list || [] : []))
  const totalIds = useSelector((state) => (state?.dr ? state?.dr[curriculumId]?.ids || [] : []))
  const totalList = useSelector((state) => (state?.dr ? state?.dr[curriculumId]?.totalList || 0 : 0))

  const skip = useSelector((state) => state?.datatable?.ul?.skip || 0)
  const rowsPerPage = useSelector((state) => state?.datatable?.ul?.rowsPerPage || 50)
  const columnsTable = useSelector((state) => state?.datatable?.ul?.[curriculumId] || [])
  useEffect(() => {
    getDevelopmentalRubrics(curriculumId, skip, rowsPerPage, {
      ...state?.filter,
      isLinked: 'yes'
    })
  }, [curriculumId, rowsPerPage, skip, state, state?.filter])
  useEffect(() => {
    const columns = [
      {
        label: '',
        key: 'status',
        align: 'left',
        show: true,
        hide: true,
        position: 0,
        sort: false,
        width: 50
      },
      {
        label: 'Name',
        key: 'name',
        align: 'left',
        width: 500,
        show: true,
        hide: true,
        position: 1,
        sort: true
      },
      {
        label: 'Year level',
        key: 'yearLevel',
        align: 'left',
        width: 250,
        show: true,
        hide: true,
        position: 2,
        sort: true
      },
      { label: 'Period', key: 'period', align: 'center', width: 200, show: true, hide: true, position: 7 }
      // {
      //   label: '# Elements',
      //   key: 'lenGS',
      //   align: 'center',
      //   className: 'text-center',
      //   show: true,
      //   width: 80,
      //   hide: true,
      //   position: 8
      // },
      // {
      //   label: '# Worked examples',
      //   key: 'lenWE',
      //   align: 'center',
      //   className: 'text-center',
      //   show: true,
      //   width: 80,
      //   hide: true,
      //   position: 9
      // },
      // {
      //   label: '# Growth goals',
      //   key: 'lenGG',
      //   align: 'center',
      //   className: 'text-center',
      //   show: true,
      //   width: 80,
      //   hide: true,
      //   position: 10
      // }
    ]

    if (currentCurriculum) {
      const elements = {}
      if (currentCurriculum.elementsStructure) {
        elements[currentCurriculum.elementsStructure?._id] = {
          label: currentCurriculum.elementsStructure?.name,
          key: 'elementId',
          show: true,
          caret: true,
          hide: true,
          position: 3,
          width: 100,
          className: 'text-nowrap',
          sort: true
        }

        const subElement = currentCurriculum.elementsStructure?.children[0]
        if (subElement) {
          elements[subElement?._id] = {
            label: subElement?.name,
            key: 'subElementId',
            show: true,
            caret: true,
            hide: true,
            position: 4,
            width: 100,
            className: 'text-nowrap',
            sort: true
          }
        }
      }

      Object.keys(elements).forEach((key) => {
        columns.push(elements[key])
      })
    }

    if (subjects.length > 0) {
      columns.push({
        label: 'Subject',
        key: 'subjectId',
        align: 'left',
        width: 250,
        show: true,
        hide: true,
        position: 5,
        sort: true
      })
    }

    columns.sort((a, b) => a.position - b.position)
    dispatch(updateColumnTableCurriculum(currentCurriculum._id, 'ul', columns, true))
  }, [subjects, currentCurriculum])

  return (
    <>
      <DataGrid
        id='unit-of-learning'
        nameModule='ul'
        actionsSetting={
          checkIds.length > 0 && (
            <Typography color={'primary'} className='flex gap-2 items-center px-4'>
              {checkIds.length} selected
            </Typography>
          )
        }
        curriculumId={curriculumId}
        rows={list.map((row, index) => {
          const subject = subjects.find((item) => item._id === row.subjectId)
          const elements = getObjectCurriculumElementsDR(
            currentCurriculum?.elements,
            currentCurriculum?.elementsStructure,
            row?.curriculumElements || {}
          )
          const yearLevel = row.yearLevel?.map((item) => years.find((year) => year._id === item)?.name)
          const textLinkedClasses = row?.linkedClasses?.length > 0 ? 'unlink' : 'link'
          const st = status.find((item) => item.code === row.status)
          return {
            ...row,
            index: index + 1 + rowsPerPage * skip,
            elementId: elements?.element?.valueName || '',
            subElementId: elements?.subElement?.valueName || '',
            status: (
              <div className='flex items-center gap-2 justify-center min-w-60px'>
                {row?.status ? (
                  <ChipStatus
                    status={st?.name || row?.status || 'No status'}
                    color={st?.color || 'var(--muted)'}
                    bgColor={st?.bgColor || 'var(--muted-foreground)'}
                    border={st?.border || 'var(--muted)'}
                    size='small'
                  />
                ) : (
                  <ChipStatus
                    status={'No status'}
                    color={'var(--muted)'}
                    bgColor={'var(--muted-foreground)'}
                    border={'var(--muted)'}
                    size='small'
                  />
                )}
              </div>
            ),
            subjectId: subject?.name || '',
            period: (
              <TextFormatDateRange
                colorText={row?.linkedClasses?.length > 0 ? 'text-accent' : 'text-primary opacity-50'}
                className='text-sm text-nowrap'
                startDate={row.startDate || ''}
                endDate={row.endDate || ''}
              />
            ),
            yearLevel: yearLevel?.join(', ') || '',
            checkIds: (
              <Checkbox
                id={`checkbox-id-user-${row._id}`}
                checked={checkIds?.includes(row._id)}
                onChange={(e) => onSelected(row._id, e.target.checked)}
                color='primary'
              />
            ),
            actions: (
              <div className='flex items-center gap-1 items-center justify-center'>
                {permissions?.edit && (
                  <IconTooltipButton
                    id={`edit-${row._id}`}
                    disabled={isLoadingList}
                    title='Edit'
                    icon='edit'
                    onClick={() => onEdit(row)}
                  />
                )}
              </div>
            )
          }
        })}
        loading={isLoadingList}
        totalRows={totalList}
        skip={skip}
        rowsPerPage={rowsPerPage}
        columns={[
          { label: '#', key: 'index', align: 'center', width: 10, show: true, hide: false },
          {
            show: true,
            key: 'checkIds',
            align: 'center',
            hide: false,
            width: 15,
            label: (
              <Checkbox
                id={`checkbox-id-user-header`}
                checked={checkIds?.length > 0 && checkIds?.length === totalIds?.length}
                indeterminate={checkIds?.length > 0 && checkIds?.length < totalIds?.length}
                onChange={(e) => onSelectedAll(e.target.checked)}
                color='primary'
                disabled={totalIds?.length === 0 || isLoadingList}
              />
            )
          },
          ...columnsTable,

          { label: 'Actions', key: 'actions', align: 'center', width: 50, show: true, hide: false }
        ]}
        labelNoRows="You don't have any unit of learning yet"
      />
    </>
  )
}

export default List
